import { useState } from 'react';
import type { FormEvent, JSX } from 'react';

import type { RevealCredentialsNeeded } from '../../lib/revealStaging';

// The Alarms stage's NON-BLOCKING credentials sub-beat (config-harvest B3):
// the boat's inline config-harvest ran ahead of alarm generation (Reveal.tsx
// / core source-plan.ts's generateAlarmRecommendationsAfterPush) and found
// gateways it couldn't read without a device login. Lodging a bundle here
// relays the EXISTING `setup_harvest_credentials` verb, which re-triggers the
// harvest boat-side; the caller (Reveal.tsx) re-fires the gateway-plan apply
// once that round trip completes so a rec set built from the richer config
// can supersede what's already showing.
//
// Deliberately never blocks the stage: "Start watching" / "Continue to my
// boat" are never gated on this, and declining a row ("Skip") fires NO verb —
// it just moves on, mirroring the reveal's other soft affordances.
//
// This component only reports generic { username, password } values — the
// page maps them onto the actual wire bundle keys the harvest adapters read
// (`admin_user` / `admin_password`, the ONLY fields any adapter currently
// consumes — core `setup/adapters/yacht-devices.ts` `basicAuthHeader`) right
// before the relay call. The form is labelled generically since
// `needsCredentials[]` carries no per-vendor field schema (contract
// authority: it's vendor/host/id only, no secret material) — this is the
// best a vendor-agnostic prompt can do without a core change to describe the
// exact fields a given adapter wants.

const VENDOR_LABELS: Record<string, string> = {
  actisense: 'Actisense',
  maretron: 'Maretron',
  yacht_devices: 'Yacht Devices',
  digital_yacht: 'Digital Yacht',
  garmin: 'Garmin',
  raymarine: 'Raymarine',
  furuno: 'Furuno',
  victron: 'Victron',
  navico: 'Navico',
  signalk: 'Signal K',
  generic: 'Gateway',
};

function gatewayLabel(entry: RevealCredentialsNeeded): string {
  const vendor = entry.vendor ? (VENDOR_LABELS[entry.vendor] ?? entry.vendor) : null;
  if (vendor && entry.host) return `${vendor} (${entry.host})`;
  if (vendor) return vendor;
  if (entry.host) return entry.host;
  return 'A gateway';
}

export interface RevealCredentialsBundle {
  username: string;
  password: string;
}

export interface RevealCredentialsProps {
  /** The gateways still awaiting a login (pure selector output, revealStaging credentialsNeeded). */
  needed: RevealCredentialsNeeded[];
  /** The gatewayId currently mid-lodge, if any — single-flight, mirrors the arming sequence. */
  pendingGatewayId: string | null;
  /** GatewayIds that have completed a lodge this session (swap to a "saved" row). */
  lodgedGatewayIds: ReadonlySet<string>;
  /** Lodge a bundle for one gateway — the page owns the relay + correlation. */
  onSubmit: (gatewayId: string, bundle: RevealCredentialsBundle) => void;
}

export function RevealCredentials({
  needed,
  pendingGatewayId,
  lodgedGatewayIds,
  onSubmit,
}: RevealCredentialsProps): JSX.Element | null {
  if (needed.length === 0) return null;

  return (
    <div className="reveal-credentials" data-testid="reveal-credentials">
      <p className="reveal-credentials__lead">
        A few gateways need a login before we can read their full configuration — this is optional,
        your alarms already work without it.
      </p>
      <ul className="reveal-credentials__list">
        {needed.map((entry) => (
          <RevealCredentialRow
            key={entry.gatewayId}
            entry={entry}
            pending={pendingGatewayId === entry.gatewayId}
            lodged={lodgedGatewayIds.has(entry.gatewayId)}
            onSubmit={onSubmit}
          />
        ))}
      </ul>
    </div>
  );
}

function RevealCredentialRow({
  entry,
  pending,
  lodged,
  onSubmit,
}: {
  entry: RevealCredentialsNeeded;
  pending: boolean;
  lodged: boolean;
  onSubmit: RevealCredentialsProps['onSubmit'];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  if (lodged) {
    return (
      <li className="reveal-credentials__item" data-testid={`reveal-credential-${entry.gatewayId}`}>
        <span className="reveal-credentials__name">{gatewayLabel(entry)}</span>
        <span className="reveal-credentials__status">Saved — reading your gateway again…</span>
      </li>
    );
  }

  if (skipped) {
    return (
      <li className="reveal-credentials__item" data-testid={`reveal-credential-${entry.gatewayId}`}>
        <span className="reveal-credentials__name">{gatewayLabel(entry)}</span>
        <span className="reveal-credentials__status">Skipped</span>
      </li>
    );
  }

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onSubmit(entry.gatewayId, { username, password });
  }

  return (
    <li className="reveal-credentials__item" data-testid={`reveal-credential-${entry.gatewayId}`}>
      <div className="reveal-credentials__head">
        <span className="reveal-credentials__name">{gatewayLabel(entry)}</span>
        {!open ? (
          <div className="reveal-credentials__actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setOpen(true)}
              data-testid={`reveal-credential-add-${entry.gatewayId}`}
            >
              Add login
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setSkipped(true)}
              data-testid={`reveal-credential-skip-${entry.gatewayId}`}
            >
              Skip
            </button>
          </div>
        ) : null}
      </div>
      {open ? (
        <form className="reveal-credentials__form" onSubmit={submit}>
          <div className="form-group">
            <label htmlFor={`reveal-credential-username-${entry.gatewayId}`}>Username</label>
            <input
              type="text"
              id={`reveal-credential-username-${entry.gatewayId}`}
              className="form-input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid={`reveal-credential-username-${entry.gatewayId}`}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`reveal-credential-password-${entry.gatewayId}`}>Password</label>
            <input
              type="password"
              id={`reveal-credential-password-${entry.gatewayId}`}
              className="form-input"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid={`reveal-credential-password-${entry.gatewayId}`}
            />
          </div>
          <div className="reveal-credentials__actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={pending}
              data-testid={`reveal-credential-save-${entry.gatewayId}`}
            >
              {pending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setOpen(false)}
              disabled={pending}
              data-testid={`reveal-credential-cancel-${entry.gatewayId}`}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
